import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import {
    catchError,
    exhaustMap,
    forkJoin,
    map,
    Observable,
    of,
    switchMap,
} from "rxjs";
import { FormsApiService } from "../data-access/forms-api.service";
import {
    FormQuestion,
    FormQuestionDraft,
    ManagedForm,
} from "../models/form.models";
import { FormsActions } from "./forms.actions";

@Injectable()
export class FormsEffects {
    private readonly actions$ = inject(Actions);
    private readonly api = inject(FormsApiService);

    readonly open$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FormsActions.pageOpened),
            switchMap(() =>
                forkJoin({
                    forms: this.api.loadForms(),
                    subsections: this.api.loadSubsections(),
                }).pipe(
                    map((data) => FormsActions.dataLoaded(data)),
                    catchError(() => of(FormsActions.dataLoadFailed()))
                )
            )
        )
    );

    readonly create$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FormsActions.createRequested),
            exhaustMap(({ title }) =>
                this.api.createForm(title).pipe(
                    map((form) => FormsActions.createSucceeded({ form })),
                    catchError(() => of(FormsActions.mutationFailed()))
                )
            )
        )
    );

    readonly duplicate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FormsActions.duplicateRequested),
            exhaustMap(({ form }) =>
                this.api.duplicateForm(form).pipe(
                    map((copy) =>
                        FormsActions.duplicateSucceeded({ form: copy })
                    ),
                    catchError(() => of(FormsActions.mutationFailed()))
                )
            )
        )
    );

    readonly reloadDuplicatedForm$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FormsActions.duplicateSucceeded),
            map(() => FormsActions.pageOpened())
        )
    );

    readonly updateForm$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FormsActions.updateFormRequested),
            exhaustMap(({ form, mutationType }) =>
                this.api.updateForm(form).pipe(
                    map((saved) =>
                        FormsActions.updateFormSucceeded({
                            form: saved,
                            mutationType,
                        })
                    ),
                    catchError(() => of(FormsActions.mutationFailed()))
                )
            )
        )
    );

    readonly delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FormsActions.deleteRequested),
            exhaustMap(({ formId }) =>
                this.api.deleteForm(formId).pipe(
                    map(() => FormsActions.deleteSucceeded({ formId })),
                    catchError(() => of(FormsActions.mutationFailed()))
                )
            )
        )
    );

    readonly createSubsection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FormsActions.createSubsectionRequested),
            exhaustMap(({ form, title }) =>
                this.api.createSubsection(title).pipe(
                    switchMap((subsection) => {
                        const updated = {
                            ...form,
                            formSubsectionsIds: [
                                ...form.formSubsectionsIds,
                                subsection.gmvid,
                            ],
                        };
                        return this.api.updateForm(updated).pipe(
                            map((saved) =>
                                FormsActions.createSubsectionSucceeded({
                                    form: saved,
                                    subsection,
                                })
                            )
                        );
                    }),
                    catchError(() => of(FormsActions.mutationFailed()))
                )
            )
        )
    );

    readonly updateSubsection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FormsActions.updateSubsectionRequested),
            exhaustMap(({ form, subsection }) =>
                this.api.updateSubsection(form.gmvid, subsection).pipe(
                    map((saved) =>
                        FormsActions.updateSubsectionSucceeded({
                            subsection: saved,
                            mutationType: "update-subsection",
                        })
                    ),
                    catchError(() => of(FormsActions.mutationFailed()))
                )
            )
        )
    );

    readonly deleteSubsection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FormsActions.deleteSubsectionRequested),
            exhaustMap(({ form, subsectionId }) => {
                const updated = {
                    ...form,
                    formSubsectionsIds: form.formSubsectionsIds.filter(
                        (id) => id !== subsectionId
                    ),
                };
                return this.api.deleteSubsection(subsectionId, form.gmvid).pipe(
                    switchMap(() => this.api.updateForm(updated)),
                    map((saved) =>
                        FormsActions.deleteSubsectionSucceeded({
                            form: saved,
                            subsectionId,
                        })
                    ),
                    catchError(() => of(FormsActions.mutationFailed()))
                );
            })
        )
    );

    readonly reorderSubsections$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FormsActions.reorderSubsectionsRequested),
            exhaustMap(({ form, previousIndex, currentIndex }) => {
                const ids = [...form.formSubsectionsIds];
                const [moved] = ids.splice(previousIndex, 1);
                if (!moved) return of(FormsActions.mutationFailed());
                ids.splice(currentIndex, 0, moved);
                const updated = { ...form, formSubsectionsIds: ids };
                return this.api.updateForm(updated).pipe(
                    map((saved) =>
                        FormsActions.updateFormSucceeded({
                            form: saved,
                            mutationType: "reorder-subsections",
                        })
                    ),
                    catchError(() => of(FormsActions.mutationFailed()))
                );
            })
        )
    );

    readonly saveQuestion$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FormsActions.questionSaveRequested),
            exhaustMap(({ form, subsection, questionIndex, draft }) =>
                this.imageUrl(subsection.gmvid, draft).pipe(
                    switchMap((image) => {
                        const question = this.buildQuestion(draft, image);
                        const questions = [...subsection.formQuestions];
                        if (questionIndex === null) questions.push(question);
                        else questions[questionIndex] = question;
                        return this.api.updateSubsection(form.gmvid, {
                            ...subsection,
                            formQuestions: questions,
                        });
                    }),
                    map((saved) =>
                        FormsActions.updateSubsectionSucceeded({
                            subsection: saved,
                            mutationType:
                                questionIndex === null
                                    ? "create-question"
                                    : "update-question",
                        })
                    ),
                    catchError(() => of(FormsActions.mutationFailed()))
                )
            )
        )
    );

    readonly deleteQuestion$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FormsActions.deleteQuestionRequested),
            exhaustMap(({ form, subsection, questionIndex }) =>
                this.api
                    .updateSubsection(form.gmvid, {
                        ...subsection,
                        formQuestions: subsection.formQuestions.filter(
                            (_, index) => index !== questionIndex
                        ),
                    })
                    .pipe(
                        map((saved) =>
                            FormsActions.updateSubsectionSucceeded({
                                subsection: saved,
                                mutationType: "delete-question",
                            })
                        ),
                        catchError(() => of(FormsActions.mutationFailed()))
                    )
            )
        )
    );

    readonly reorderQuestions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FormsActions.reorderQuestionsRequested),
            exhaustMap(({ form, subsection, previousIndex, currentIndex }) => {
                const questions = [...subsection.formQuestions];
                const [moved] = questions.splice(previousIndex, 1);
                if (!moved) return of(FormsActions.mutationFailed());
                questions.splice(currentIndex, 0, moved);
                return this.api
                    .updateSubsection(form.gmvid, {
                        ...subsection,
                        formQuestions: questions,
                    })
                    .pipe(
                        map((saved) =>
                            FormsActions.updateSubsectionSucceeded({
                                subsection: saved,
                                mutationType: "reorder-questions",
                            })
                        ),
                        catchError(() => of(FormsActions.mutationFailed()))
                    );
            })
        )
    );

    readonly saveSignatures$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FormsActions.signaturesSaveRequested),
            exhaustMap(({ form, signatures }) =>
                this.api
                    .updateForm({ ...form, signatureSections: signatures })
                    .pipe(
                        map((saved) =>
                            FormsActions.updateFormSucceeded({
                                form: saved,
                                mutationType: "update-signatures",
                            })
                        ),
                        catchError(() => of(FormsActions.mutationFailed()))
                    )
            )
        )
    );

    private imageUrl(
        subsectionId: string,
        draft: FormQuestionDraft
    ): Observable<string> {
        return draft.file
            ? this.api.uploadQuestionImage(draft.file, subsectionId)
            : of(draft.existingImage);
    }

    private buildQuestion(
        draft: FormQuestionDraft,
        image: string
    ): FormQuestion {
        const base: FormQuestion = {
            gemQuestionSort: draft.type,
            answerSort: draft.type === "remark" ? "text" : draft.answerType,
            questions: [draft.question],
            placeholder: "",
            mandatory: draft.type === "image" ? false : draft.mandatory,
            answers: draft.answers,
            table: draft.table,
        };
        if (draft.type === "list_multiple_choice")
            return { ...base, questions: draft.prompts };
        if (draft.type === "image")
            return {
                ...base,
                questions: image ? [image] : [],
                placeholder: draft.question,
            };
        return base;
    }
}
